import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {TextField} from "@khanacademy/wonder-blocks-form";
import {LabeledField} from "@khanacademy/wonder-blocks-labeled-field";
import {sizing} from "@khanacademy/wonder-blocks-tokens";
import {HeadingSmall} from "@khanacademy/wonder-blocks-typography";
import {NICKNAME_MAX_LENGTH} from "@khan/auth-password";
import {RequestInfo} from "@khan/shared-core";

import RegionSelector from "./region-selector.tsx";
import {type BasicSettingsData} from "./types/account-settings-types.ts";

type BasicSettings = Pick<BasicSettingsData, "nickname" | "region">;

interface Props {
    settings: BasicSettings;
    onSettingsChange: (newSettings: Partial<BasicSettings>) => void;
    nicknameError: string | null | undefined;
    usernameError?: string | null | undefined;
    disabled: boolean;
    targetIsChildAccount?: boolean;
}

const BasicSettingsForm: React.FC<Props> = ({
    settings,
    onSettingsChange,
    nicknameError,
    disabled,
    targetIsChildAccount,
}) => {
    const {nickname, region} = settings;

    const targetIsOnEnglishSiteInUS =
        RequestInfo.kaLocale === "en" && RequestInfo.remoteCountry === "US";

    return (
        <View style={{gap: sizing.size_160}}>
            <HeadingSmall tag="h2">{t`Basics`}</HeadingSmall>
            {/** Real name / nickname */}
            {!targetIsChildAccount && (
                <LabeledField
                    field={
                        <TextField
                            testId="settings-nickname"
                            id="nickname"
                            value={nickname || ""}
                            onChange={(newValue) =>
                                onSettingsChange({
                                    nickname: newValue.substring(
                                        0,
                                        NICKNAME_MAX_LENGTH,
                                    ),
                                })
                            }
                            validate={() => nicknameError}
                            disabled={disabled}
                            style={styles.nicknameField}
                        />
                    }
                    label={t`Name`}
                    description={t`This is how your name will appear around Khan Academy, and how your friends and coaches will recognize you.`}
                />
            )}
            {/** Region */}
            {targetIsOnEnglishSiteInUS && (
                <View style={styles.regionSelectorWrapper}>
                    <RegionSelector
                        selectedRegionCode={region}
                        onChange={(region) => onSettingsChange({region})}
                    />
                </View>
            )}
        </View>
    );
};

export default React.memo(BasicSettingsForm);

const styles = StyleSheet.create({
    nicknameField: {
        maxInlineSize: 400,
    },
    regionSelectorWrapper: {
        maxWidth: 400,
        width: "100%",
    },
});
